import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getServices } from '../firebase.js';
import TournamentBracket from '../components/TournamentBracket.js';
import { motion } from 'framer-motion';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, increment, serverTimestamp, setDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext.js';
import { canRegisterForTournament, isParticipantInTournament, canEditTournament } from '../utils/authUtils.js';
import toast from 'react-hot-toast';

const Tournament = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bracket');
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isParticipant, setIsParticipant] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  
  const { db } = getServices();
  const { currentUser } = useAuth();
  
  // Check if the current user can edit this tournament
  useEffect(() => {
    const checkPermissions = async () => {
      if (!currentUser || !id) return;
      
      try {
        // Check if user is a participant
        const participantStatus = await isParticipantInTournament(currentUser.uid, id);
        setIsParticipant(participantStatus);
        
        // Check if user can edit the tournament
        const canEditStatus = await canEditTournament(currentUser.uid, id);
        setCanEdit(canEditStatus);
      } catch (error) {
        console.error('Error checking permissions:', error);
      }
    };
    
    checkPermissions();
  }, [currentUser, id]);
  
  // Fetch tournament data
  useEffect(() => {
    const fetchTournament = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const tournamentRef = doc(db, 'tournaments', id);
        const tournamentDoc = await getDoc(tournamentRef);
        
        if (!tournamentDoc.exists()) {
          setError('Tournament not found');
          setLoading(false);
          return;
        }
        
        const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
        
        // Format dates
        if (tournamentData.startDate && typeof tournamentData.startDate.toDate === 'function') {
          tournamentData.startDateFormatted = tournamentData.startDate.toDate().toLocaleDateString();
        }
        
        if (tournamentData.endDate && typeof tournamentData.endDate.toDate === 'function') {
          tournamentData.endDateFormatted = tournamentData.endDate.toDate().toLocaleDateString();
        }
        
        if (tournamentData.registrationDeadline && typeof tournamentData.registrationDeadline.toDate === 'function') {
          tournamentData.registrationDeadlineFormatted = tournamentData.registrationDeadline.toDate().toLocaleDateString();
        }
        
        setTournament(tournamentData);
        
        // Fetch teams if participants exist
        if (tournamentData.participants && tournamentData.participants.length > 0) {
          fetchTeams(tournamentData.participants);
        }
        
        // Track view
        if (currentUser) {
          // Log tournament view
          const viewRef = doc(db, 'tournaments', id, 'views', currentUser.uid);
          updateDoc(viewRef, {
            viewedAt: serverTimestamp(),
            userId: currentUser.uid
          }).catch(() => {
            // If the document doesn't exist, create it
            getDoc(viewRef).catch(() => {
              setDoc(viewRef, {
                viewedAt: serverTimestamp(),
                userId: currentUser.uid,
                count: 1
              });
            });
          });
        }
      } catch (error) {
        console.error('Error fetching tournament:', error);
        setError(`Failed to load tournament data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchTournament();
    } else {
      setError('Invalid tournament ID');
      setLoading(false);
    }
  }, [id, db, currentUser]);
  
  // Fetch teams data
  const fetchTeams = useCallback(async (participantIds) => {
    try {
      // For team-based tournaments, fetch team data
      const fetchedTeams = [];
      
      for (const participantId of participantIds) {
        try {
          // Try to fetch team data first
          const teamDocRef = doc(db, 'teams', participantId);
          const teamSnapshot = await getDoc(teamDocRef);
          
          if (teamSnapshot.exists()) {
            fetchedTeams.push({
              id: teamSnapshot.id,
              ...teamSnapshot.data()
            });
          } else {
            // Try to fetch user data if not a team
            const userDocRef = doc(db, 'users', participantId);
            const userSnapshot = await getDoc(userDocRef);
            
            if (userSnapshot.exists()) {
              fetchedTeams.push({
                id: userSnapshot.id,
                name: userSnapshot.data().username || userSnapshot.data().displayName,
                ...userSnapshot.data()
              });
            }
          }
        } catch (participantError) {
          console.error(`Error fetching participant ${participantId}:`, participantError);
        }
      }
      
      setTeams(fetchedTeams);
    } catch (error) {
      console.error('Error fetching teams/users:', error);
    }
  }, [db]);
  
  // Handle joining tournament
  const handleJoinTournament = async () => {
    if (!currentUser) {
      toast.error('You must be logged in to join a tournament');
      return;
    }
    
    try {
      setIsRegistering(true);
      
      // Check if user can register
      const { canRegister, reason } = await canRegisterForTournament(currentUser.uid, id);
      
      if (!canRegister) {
        toast.error(reason || 'Cannot join this tournament');
        return;
      }
      
      // Get tournament reference
      const tournamentRef = doc(db, 'tournaments', id);
      
      // Update tournament document to add user to participants
      await updateDoc(tournamentRef, {
        participants: arrayUnion(currentUser.uid),
        currentTeams: increment(1),
        updatedAt: serverTimestamp()
      });
      
      // Add to participants subcollection
      const participantRef = doc(db, 'tournaments', id, 'participants', currentUser.uid);
      await setDoc(participantRef, {
        userId: currentUser.uid,
        displayName: currentUser.displayName || currentUser.email,
        joinedAt: serverTimestamp(),
        role: 'participant',
        status: 'active'
      });
      
      toast.success('Successfully joined tournament!');
      setIsParticipant(true);
      
      // Update local tournament data
      setTournament(prev => ({
        ...prev,
        participants: [...(prev.participants || []), currentUser.uid],
        currentTeams: (prev.currentTeams || 0) + 1
      }));
    } catch (error) {
      console.error('Error joining tournament:', error);
      toast.error('Failed to join tournament: ' + error.message);
    } finally {
      setIsRegistering(false);
    }
  };
  
  // Handle leaving tournament
  const handleLeaveTournament = async () => {
    if (!currentUser) {
      toast.error('You must be logged in');
      return;
    }
    
    try {
      setIsRegistering(true);
      
      // Get tournament reference
      const tournamentRef = doc(db, 'tournaments', id);
      const tournamentDoc = await getDoc(tournamentRef);
      
      if (!tournamentDoc.exists()) {
        toast.error('Tournament not found');
        return;
      }
      
      const tournamentData = tournamentDoc.data();
      
      // Check if tournament has already started
      if (tournamentData.status === 'in_progress' || tournamentData.status === 'completed') {
        toast.error('Cannot leave an active or completed tournament');
        return;
      }
      
      // Update tournament document to remove user from participants
      await updateDoc(tournamentRef, {
        participants: arrayRemove(currentUser.uid),
        currentTeams: increment(-1),
        updatedAt: serverTimestamp()
      });
      
      // Remove from participants subcollection
      const participantRef = doc(db, 'tournaments', id, 'participants', currentUser.uid);
      await deleteDoc(participantRef);
      
      toast.success('Successfully left tournament');
      setIsParticipant(false);
      
      // Update local tournament data
      setTournament(prev => ({
        ...prev,
        participants: (prev.participants || []).filter(uid => uid !== currentUser.uid),
        currentTeams: Math.max(0, (prev.currentTeams || 0) - 1)
      }));
    } catch (error) {
      console.error('Error leaving tournament:', error);
      toast.error('Failed to leave tournament: ' + error.message);
    } finally {
      setIsRegistering(false);
    }
  };
  
  // Handle bracket updates
  const handleBracketsUpdate = async () => {
    try {
      // Refresh tournament data to get updated brackets
      const tournamentRef = doc(db, 'tournaments', id);
      const tournamentDoc = await getDoc(tournamentRef);
      
      if (tournamentDoc.exists()) {
        const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
        
        // Format dates
        if (tournamentData.startDate && typeof tournamentData.startDate.toDate === 'function') {
          tournamentData.startDateFormatted = tournamentData.startDate.toDate().toLocaleDateString();
        }
        
        if (tournamentData.endDate && typeof tournamentData.endDate.toDate === 'function') {
          tournamentData.endDateFormatted = tournamentData.endDate.toDate().toLocaleDateString();
        }
        
        setTournament(tournamentData);
        toast.success('Bracket updated successfully');
      }
    } catch (error) {
      console.error('Error refreshing brackets:', error);
      toast.error('Failed to refresh bracket data');
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-killa-primary"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-red-500 mb-4">Error</h2>
        <p className="text-gray-400">{error}</p>
        <button 
          onClick={() => navigate('/tournaments')}
          className="mt-4 px-4 py-2 bg-killa-primary text-white rounded-md hover:bg-killa-primary-dark"
        >
          Back to Tournaments
        </button>
      </div>
    );
  }
  
  if (!tournament) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Tournament not found</p>
        <button 
          onClick={() => navigate('/tournaments')}
          className="mt-4 px-4 py-2 bg-killa-primary text-white rounded-md hover:bg-killa-primary-dark"
        >
          Back to Tournaments
        </button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Tournament Header */}
      <motion.div 
        className="card"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold mb-2">{tournament.name}</h1>
            <div className="flex items-center space-x-4 text-gray-400">
              <span>{tournament.teamSize}v{tournament.teamSize}</span>
              <span>•</span>
              <span>Prize Pool: {tournament.prizePool.toLocaleString()} KILLA</span>
              <span>•</span>
              <span>Entry: {tournament.entryFee} KILLA</span>
            </div>
            {tournament.description && (
              <p className="mt-2 text-gray-300">{tournament.description}</p>
            )}
            <div className="mt-2 flex items-center">
              <span className={`px-2 py-1 rounded text-xs uppercase tracking-wider ${
                tournament.status === 'upcoming' ? 'bg-blue-500/20 text-blue-400' :
                tournament.status === 'in_progress' ? 'bg-green-500/20 text-green-400' :
                'bg-gray-500/20 text-gray-400'
              }`}>
                {tournament.status === 'upcoming' ? 'Upcoming' :
                 tournament.status === 'in_progress' ? 'Live' :
                 'Completed'}
              </span>
              <span className="ml-2 text-gray-400 text-sm">
                {tournament.startDateFormatted} - {tournament.endDateFormatted}
              </span>
              {tournament.registrationDeadlineFormatted && (
                <span className="ml-2 text-gray-400 text-sm">
                  Registration Deadline: {tournament.registrationDeadlineFormatted}
                </span>
              )}
            </div>
          </div>
          <div className="flex space-x-4">
            {tournament.status === 'upcoming' && currentUser && (
              isParticipant ? (
                <button 
                  className="btn-secondary"
                  onClick={handleLeaveTournament}
                  disabled={isRegistering}
                >
                  {isRegistering ? 'Processing...' : 'Leave Tournament'}
                </button>
              ) : (
                <button 
                  className="btn-primary"
                  onClick={handleJoinTournament}
                  disabled={isRegistering}
                >
                  {isRegistering ? 'Processing...' : 'Join Tournament'}
                </button>
              )
            )}
            {!currentUser && tournament.status === 'upcoming' && (
              <button 
                className="btn-primary"
                onClick={() => navigate('/login')}
              >
                Sign in to Join
              </button>
            )}
            <button className="btn-secondary">Share</button>
          </div>
        </div>
      </motion.div>

      {/* Tournament Navigation */}
      <div className="card">
        <div className="flex space-x-4 border-b border-gray-800">
          <button
            className={`pb-4 px-4 ${
              activeTab === 'bracket'
                ? 'text-killa-primary border-b-2 border-killa-primary'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setActiveTab('bracket')}
          >
            Bracket
          </button>
          <button
            className={`pb-4 px-4 ${
              activeTab === 'streams'
                ? 'text-killa-primary border-b-2 border-killa-primary'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setActiveTab('streams')}
          >
            Streams
          </button>
          <button
            className={`pb-4 px-4 ${
              activeTab === 'teams'
                ? 'text-killa-primary border-b-2 border-killa-primary'
                : 'text-gray-400 hover:text-white'
            }`}
            onClick={() => setActiveTab('teams')}
          >
            Teams
          </button>
        </div>

        {/* Bracket View */}
        {activeTab === 'bracket' && (
          <div className="mt-6">
            <TournamentBracket 
              brackets={tournament.brackets || []} 
              format={tournament.format}
              teams={teams}
              participants={tournament.participants || []}
              tournamentId={id}
              tournament={tournament}
              onBracketsUpdate={handleBracketsUpdate}
            />
          </div>
        )}

        {/* Streams View */}
        {activeTab === 'streams' && (
          <div className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tournament.streams && tournament.streams.length > 0 ? (
                tournament.streams.map((stream, index) => (
                  <div key={`stream-${index}`} className="bg-killa-background rounded-lg overflow-hidden">
                    <div className="aspect-video bg-gray-900">
                      {stream.url ? (
                        <iframe 
                          src={stream.url} 
                          className="w-full h-full" 
                          title={`Stream ${index}`}
                          allowFullScreen
                        ></iframe>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                          Stream Offline
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold">{stream.title || `Match ${index + 1}`}</h3>
                      <p className="text-sm text-gray-400">{stream.description || 'No description'}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-8 text-gray-400">
                  No streams available for this tournament
                </div>
              )}
            </div>
          </div>
        )}

        {/* Teams View */}
        {activeTab === 'teams' && (
          <div className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teams.length > 0 ? (
                teams.map((team) => (
                  <div key={team.id} className="bg-killa-background p-4 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-700 rounded-full overflow-hidden">
                        {team.logo || team.photoURL ? (
                          <img 
                            src={team.logo || team.photoURL} 
                            alt={team.name} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white text-xl font-bold">
                            {team.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold">{team.name}</h3>
                        <p className="text-sm text-gray-400">
                          {team.members ? `${team.members.length} members` : 'Solo Player'}
                        </p>
                      </div>
                    </div>
                    {team.stats && (
                      <div className="mt-4">
                        <div className="flex justify-between text-sm">
                          <span>Wins</span>
                          <span className="text-killa-primary">{team.stats.wins || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Losses</span>
                          <span className="text-red-500">{team.stats.losses || 0}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-8 text-gray-400">
                  No teams registered for this tournament
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tournament Chat */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Tournament Chat</h2>
        <div className="h-64 bg-killa-background rounded-lg p-4 overflow-y-auto">
          <div className="space-y-4">
            <div className="flex space-x-2">
              <div className="w-8 h-8 bg-gray-700 rounded-full"></div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium">Player123</span>
                  <span className="text-xs text-gray-400">2m ago</span>
                </div>
                <p className="text-gray-300">Good luck everyone!</p>
              </div>
            </div>
            {/* Add more chat messages */}
          </div>
        </div>
        <div className="mt-4 flex space-x-4">
          <input
            type="text"
            placeholder="Type a message..."
            className="input flex-1"
            disabled={!isParticipant && !canEdit}
          />
          <button 
            className="btn-primary"
            disabled={!isParticipant && !canEdit}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default Tournament; 